import { useFeatureValue } from '@growthbook/growthbook-react';
import { CheckBoxOutlined, MoreVert } from '@mui/icons-material';
import {
  Alert,
  AlertTitle,
  Checkbox,
  IconButton,
  Menu,
  MenuList,
  Snackbar,
} from '@mui/material';
import React, { useCallback, useState, useEffect } from 'react';
import { useModal } from 'react-modal-hook';

import AddTipModal from '../TimesheetList/modals/AddTipModal';
import CommentsTimesheetModal from '../TimesheetList/modals/CommentsTimesheetModal';
import EditTimesheetModal from '../TimesheetList/modals/EditTimesheetModal';
import MapViewModal from '../TimesheetList/modals/MapViewModal';
import TimesheetLogsModal from './modals/TimesheetLogsModal';
import DismissWorkerModal from './WorkerMobileView/DismissWorkerModal';
import RemoveFromAssignmentModal from './WorkerMobileView/RemoveFromAssignmentModal';
import {
  JobStatus,
  TimeSheetStatus,
  enableCheckboxForClient,
  getTimeSheetStatus,
  getTimesheetReport,
} from '../util';

import CustomTooltipJob from './TooltipJob';
import WorkerMobileDisableView from './WorkerMobileDisableView';
import WorkerMobileView, { WorkerTimesheet } from './WorkerMobileView';
import useWorkerActions from './useWorkerActions';
import {
  ConfirmedChip,
  HiredChip,
  MenuItemsObject,
  NoShowChip,
  RemovedWorkerChip,
  RenderWorkerInfo,
  getShiftTime,
  shiftForId,
  timesheetForShift,
} from './workerUtils';

import Stack from '@/components/Stack';
import { FEATURE_TOGGLE } from '@/constants/featuretoggle';
import { TableRow, TableCell } from '@/elements/Table';
import useMediaQuery from '@/hooks/useMediaQuery';
import { getRoundOff } from '@/routes/Agency/Job/util';
import {
  Address,
  GetJobQuery,
  JobTypeEnum,
  JobWorkerStatusEnum,
  RelationshipKindEnum,
} from '@/types/graphql';
import PermissionComponent from '@/routes/PermissionComponent';
import {
  PoolRow,
  PreferredPoolsAPI,
  extractPoolsError,
} from '@/services/preferredPoolsAPI';
import { axiosRubyClient } from '@/util/axios/axiosClient';
import { trackPoolMemberAdded } from '@/util/vexo';

// types
type WorkerInfo = GetJobQuery['job']['jobWorkers'][0];
type ViolationReasonLookup = Record<
  string,
  Partial<Record<'late_drop' | 'no_show', string>>
>;
type Props = {
  job: GetJobQuery['job'];
  worker: WorkerInfo;
  jobStatus: JobStatus;
  isClientAdmin: boolean;
  setCheckboxIds: (ids: any) => void;
  checkboxIds: string[];
  isSelecting?: boolean;
  setIsSelecting?: (isSelect: boolean) => void;
  relationshipStatus: RelationshipKindEnum | null;
  createWorkerRelationship: (
    reason: string,
    comments: string,
    kind: RelationshipKindEnum,
  ) => void;
  deleteWorkerRelationship: (kind: RelationshipKindEnum) => void;
  violationReasonLookup?: ViolationReasonLookup;
  skillPool?: PoolRow | null;
  /**
   * GRAVY-10453. Which OCCURRENCE this page is showing, from `?shift=<id>`.
   *
   * Undefined is the historical behaviour and stays the default: the first
   * timesheet found for the worker, and `shifts[0]` as the edit anchor. That is
   * wrong on a multi-shift job — a worker has one timesheet per shift and
   * `.find()` returns whichever came back first — but changing it
   * unconditionally would silently move the page for every existing user, so
   * the correction is scoped to callers that actually name an occurrence.
   */
  focusedShiftId?: string | null;
  /**
   * GRAVY-10453. WHOSE row the deep link is about, from `?worker=<id>`.
   *
   * Matched on the worker's ID and never on their name: the case this exists
   * for is a shift carrying two workers with the SAME display name, where the
   * name is precisely what cannot be told apart. QA hit exactly that — two
   * "John Doe" rows on one occurrence, with nothing marking the flagged one.
   */
  focusedWorkerId?: string | null;
};

// The No Show highlight (GRAVY-10317). Same pair the Dashboard V2 No Show queue
// uses for its worker rows and status badge, so the two surfaces read as one
// treatment rather than two different reds.
const NO_SHOW_BG = '#FCE9E6';
const NO_SHOW_BORDER = '#C10000';

// GRAVY-10453. The deep-linked worker's row. The SAME pink the Review modal
// paints its flagged check-out row (`.dashv2-review-row.is-flagged`), so a flag
// reads as one treatment across the two surfaces — and a deliberately different
// pair from NO_SHOW_* above, which is a different claim about the worker.
const FLAG_FOCUS_BG = '#FFEFEC';
const FLAG_FOCUS_BORDER = '#CF040C';

// Borders go on the CELLS: a <tr> cannot paint its own border under the default
// border-collapse, so styling the row alone shows nothing. Extracted because
// GRAVY-10453 needed the identical shape in a second colour, and two copies of
// this drift.
const rowOutline = (background: string, border: string) => ({
  backgroundColor: background,
  '& > td': {
    borderTop: `1px solid ${border}`,
    borderBottom: `1px solid ${border}`,
  },
  '& > td:first-of-type': {
    borderLeft: `1px solid ${border}`,
    borderTopLeftRadius: 8,
    borderBottomLeftRadius: 8,
  },
  '& > td:last-of-type': {
    borderRight: `1px solid ${border}`,
    borderTopRightRadius: 8,
    borderBottomRightRadius: 8,
  },
});

const HiredWorkerRow = ({
  job,
  worker,
  jobStatus,
  isClientAdmin,
  checkboxIds,
  setCheckboxIds,
  isSelecting,
  setIsSelecting,
  relationshipStatus,
  createWorkerRelationship,
  deleteWorkerRelationship,
  violationReasonLookup = {},
  skillPool = null,
  focusedShiftId = null,
  focusedWorkerId = null,
}: Props) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const workerInfo = worker.worker;

  // GRAVY-10453: both keyed on the focused occurrence. See the helpers for why
  // matching a worker alone is wrong on a multi-shift job.
  const timesheet = timesheetForShift(
    job.timesheets,
    worker.id,
    focusedShiftId,
  );
  const activeShift = shiftForId(job.shifts, focusedShiftId);

  // GRAVY-10453. Is THIS the row the "Review shift" link came for?
  const isFlagFocused =
    !!focusedWorkerId && String(workerInfo?.id) === String(focusedWorkerId);
  const rowRef = React.useRef<HTMLTableRowElement | null>(null);

  // Bring it into view as well as colouring it: on a fully-staffed shift the
  // flagged row can sit below the fold, and a highlight nobody scrolls to
  // solves nothing. `block: 'center'` rather than `'start'`, so the row does
  // not land under the sticky table header.
  useEffect(() => {
    if (!isFlagFocused || !rowRef.current) return;
    rowRef.current.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
  }, [isFlagFocused]);
  const [timesheetLogs, setTimesheetLogs] = useState<any[]>([]);
  const [showTimesheetLogsModal, hideTimesheetLogsModal] = useModal(
    () => (
      <TimesheetLogsModal
        open={true}
        onClose={hideTimesheetLogsModal}
        timesheetId={Number(timesheet?.id) || 0}
        auditLogs={timesheetLogs}
      />
    ),
    [timesheetLogs, timesheet?.id],
  );
  const isRemovedWorker =
    jobStatus === JobStatus.CANCELLED ||
    worker.status === JobWorkerStatusEnum.DISMISSED;

  const isLTAJob = job.jobType === JobTypeEnum.LTA;
  const canShowDismissWorkerOption =
    !worker.dismissType && jobStatus === JobStatus.IN_PROGRESS;
  const canShowRemoveFromAssignment =
    isLTAJob &&
    (jobStatus === JobStatus.IN_PROGRESS ||
      jobStatus === JobStatus.COMPLETED) &&
    !worker.dismissType &&
    worker.status !== JobWorkerStatusEnum.DISMISSED;

  const reason = !isClientAdmin
    ? violationReasonLookup[String(worker?.id)]?.no_show ||
      worker?.dismissType ||
      worker?.dismissReason ||
      undefined
    : undefined;
  const phoneOnly = useMediaQuery('(max-width: 559px)');

  const [anchorTooltip, setAnchorTooltip] = React.useState(null);
  const handlePopoverOpen = (event) => {
    reason && setAnchorTooltip(event.currentTarget);
  };
  const handlePopoverClose = () => {
    reason && setAnchorTooltip(null);
  };
  const openTooltip = Boolean(anchorTooltip);

  const tsStatus = getTimeSheetStatus(timesheet);

  const showRating = useFeatureValue(FEATURE_TOGGLE.WorkerRating, false);

  const [showEditModal, hideEditModal] = useModal(
    () => (
      <EditTimesheetModal
        hideModal={hideEditModal}
        isCancelJobTimesheet={isRemovedWorker}
        jobAddress={job.address as Address}
        jobId={job.id}
        shift={activeShift}
        timesheet={timesheet}
        phoneOnly={phoneOnly}
        variant={
          tsStatus === TimeSheetStatus.APPROVED ||
          tsStatus === TimeSheetStatus.REJECTED
            ? 'DETAIL'
            : 'DEFAULT'
        }
        workerInfo={workerInfo}
      />
    ),
    [timesheet, tsStatus, job?.id, job?.shifts, activeShift, phoneOnly],
  );

  const {
    handleShowed,
    showRemoveModal,
    approveTimesheet,
    unRejectTimesheet,
    error,
    setError,
  } = useWorkerActions(job);

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleEditTimesheet = async () => {
    showEditModal();
  };

  const [showCommentsModal, hideCommentsModal] = useModal(
    () => (
      <CommentsTimesheetModal
        hideModal={hideCommentsModal}
        relationshipStatus={relationshipStatus}
        createWorkerRelationship={createWorkerRelationship}
        deleteWorkerRelationship={deleteWorkerRelationship}
        timesheet={timesheet!}
      />
    ),
    [
      relationshipStatus,
      createWorkerRelationship,
      deleteWorkerRelationship,
      timesheet,
    ],
  );

  const handleMapViewModal = () => {
    handleCloseMenu();
    setTimeout(() => {
      showMapViewModal();
    }, 200);
  };

  const [showMapViewModal, hideMapViewModal] = useModal(
    () => (
      <MapViewModal
        hideModal={hideMapViewModal}
        jobAddress={job.address as Address}
        timesheet={timesheet!}
      />
    ),
    [timesheet],
  );

  const getModifiedBreakMinutes = () => {
    if (
      !timesheet?.modifiedCheckinAt &&
      !timesheet?.modifiedCheckoutAt &&
      timesheet?.modifiedBreakMinutes === 0
    ) {
      return null;
    } else {
      return timesheet?.modifiedBreakMinutes ?? null;
    }
  };

  const handleApproveTimesheet = useCallback(async () => {
    const payload = {
      approvedBreakMinutes:
        getModifiedBreakMinutes() ?? timesheet?.reportedBreakMinutes ?? 0,
      approvedCheckinAt:
        timesheet?.modifiedCheckinAt ??
        timesheet?.reportedCheckinAt ??
        timesheet?.checkinAt ??
        null,
      approvedCheckoutAt:
        timesheet?.modifiedCheckoutAt ??
        timesheet?.reportedCheckoutAt ??
        timesheet?.checkoutAt ??
        null,
      ratingComment: timesheet?.reportComment,
      tipAmount: timesheet?.tipAmount ?? 0,
      timesheetId: timesheet?.id,
    };
    if (timesheet) {
      await approveTimesheet({
        variables: payload,
      });
      if (isClientAdmin) {
        setTimeout(() => {
          handleAddComment();
        }, 3000);
      }
    }
  }, [timesheet]);

  const handleUnrejectTimesheet = useCallback(async () => {
    await unRejectTimesheet({
      variables: {
        timesheetId: timesheet!.id,
      },
    });
    handleCloseMenu();
  }, [timesheet?.id]);

  const handleRemoveTimesheet = useCallback(() => {
    showRemoveModal({ id: worker.id });
    setTimeout(() => {
      handleCloseMenu();
    }, 150);
  }, [worker?.id]);

  const handleRemovedFromAssignment = useCallback(() => {
    handleCloseMenu();
    setTimeout(() => {
      showRemoveFromAssignmentModal();
    }, 200);
  }, []);

  const [showRemoveFromAssignmentModal, hideRemoveFromAssignmentModal] =
    useModal(
      () => (
        <RemoveFromAssignmentModal
          open={true}
          onClose={hideRemoveFromAssignmentModal}
          workerFullName={`${workerInfo.user?.firstName || ''} ${
            workerInfo.user?.lastName || ''
          }`.trim()}
          workerInfo={workerInfo}
          job={job}
          jobWorkerId={worker.id}
        />
      ),
      [workerInfo, job, worker.id],
    );

  const handleClose = useCallback((event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setError('');
  }, []);

  const checkOut = getTimesheetReport(
    timesheet?.approvedCheckoutAt || null,
    timesheet?.modifiedCheckoutAt || null,
    timesheet?.reportedCheckoutAt || null,
    timesheet?.checkoutAt || null,
  );

  const approvedTime = timesheet ? getShiftTime(timesheet) : 0;
  const isNoShow = !worker.showed;
  const isConfirmed = worker.status === JobWorkerStatusEnum.CONFIRMED;
  const handleToggleShowed = async () => {
    if (!isNoShow && checkboxIds.includes(worker.id)) {
      setCheckboxIds((ids) => ids.filter((i) => worker.id !== i));
    }
    await handleShowed({
      variables: { jobWorkerId: worker.id, showed: isNoShow },
    });
    handleCloseMenu();
    if (isNoShow && tsStatus === TimeSheetStatus.REJECTED && !isClientAdmin) {
      setTimeout(() => {
        handleUnrejectTimesheet();
      }, 150);
    }
  };

  const handleAddComment = () => {
    showCommentsModal();
    handleCloseMenu();
  };

  const handleViewTimesheetLogs = async () => {
    console.log('Debug - timesheet:', timesheet);
    console.log('Debug - worker.id:', worker.id);
    console.log(job.id);
    console.log('Debug - job.timesheets:', job.timesheets);

    if (!timesheet?.id) {
      console.log('No timesheet ID available - timesheet is null or undefined');
      setError(
        'No timesheet found for this worker. Cannot view timesheet logs.',
      );
      handleCloseMenu();
      return;
    }

    try {
      const response = await axiosRubyClient.get(
        `/api/admin/timesheets/${timesheet.id}/retrieve_audit_logs`,
      );
      console.log('Timesheet audit logs response:', response.data);

      if (response.data?.success && response.data?.data?.audit_logs) {
        // Sort audit logs by changed_at in descending order (most recent first)
        const sortedLogs = response.data.data.audit_logs.sort(
          (a: any, b: any) =>
            new Date(b.changed_at).getTime() - new Date(a.changed_at).getTime(),
        );
        setTimesheetLogs(sortedLogs);
        showTimesheetLogsModal();
      } else {
        console.log('No audit logs found or invalid response format');
        setTimesheetLogs([]);
        showTimesheetLogsModal();
      }
    } catch (error) {
      console.error('Error fetching timesheet audit logs:', error);
      setTimesheetLogs([]);
      showTimesheetLogsModal();
    }

    handleCloseMenu();
  };

  const handleCheckClick = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.checked) {
        setCheckboxIds((ids) => [...ids, worker.id]);
      } else {
        setCheckboxIds((ids) => ids.filter((i) => i !== worker.id));
      }
    },
    [setCheckboxIds],
  );

  const showAddTipModal = () => {
    handleCloseMenu();
    setTimeout(() => {
      handleAddTipModal();
    }, 200);
  };

  const [handleAddTipModal, hideAddTipModal] = useModal(
    () => (
      <AddTipModal
        hideModal={hideAddTipModal}
        jobId={job.id}
        timesheet={timesheet}
      />
    ),
    [timesheet],
  );

  const handleDismissWorker = () => {
    handleCloseMenu();
    setTimeout(() => {
      showDismissWorkerModal();
    }, 200);
  };

  const [showDismissWorkerModal, hideDismissWorkerModal] = useModal(
    () => (
      <DismissWorkerModal
        open={true}
        onClose={hideDismissWorkerModal}
        onConfirm={(reason, details, blockWorker) => {
          // The modal handles the API call internally
        }}
        workerFullName={`${workerInfo.user?.firstName || ''} ${
          workerInfo.user?.lastName || ''
        }`.trim()}
        workerInfo={workerInfo}
        job={job}
      />
    ),
    [workerInfo, job],
  );

  const handleUndismissWorker = useCallback(async () => {
    handleCloseMenu();
    try {
      const workerId = workerInfo?.id;
      const jobId = job?.id;

      if (!workerId) {
        console.error('Worker ID not found');
        return;
      }

      if (!jobId) {
        console.error('Shift ID not found');
        return;
      }

      await axiosRubyClient.post('/api/admin/job_workers/undismiss', {
        workerId: workerId,
        jobId: jobId,
      });

      // Reload the page to reflect the changes
      window.location.reload();
    } catch (error) {
      console.error('Error undismissing worker:', error);
      setError('Failed to undismiss worker. Please try again.');
    }
  }, [workerInfo?.id, job?.id]);

  // GRAVY-10349: one-click add to the customer's pool for the job's skill
  // (prototype addToPool — no picker; the job context implies the pool).
  const [poolNotice, setPoolNotice] = useState<{
    severity: 'success' | 'error';
    title: string;
    body?: string;
  } | null>(null);

  const handleAddToPool = async () => {
    handleCloseMenu();
    if (!skillPool) return;
    try {
      await PreferredPoolsAPI.addMember(skillPool.id, {
        worker_id: Number(workerInfo.id),
      });
      trackPoolMemberAdded({
        pool_id: skillPool.id,
        worker_id: Number(workerInfo.id),
        source: 'manual',
        reason_given: false,
        entry_point: 'hired_row',
      });
      const customerName = job.account?.customer?.name;
      setPoolNotice({
        severity: 'success',
        title: `Added to ${skillPool.skill.name} pool`,
        body: customerName ? `Now visible to ${customerName}` : undefined,
      });
    } catch (err) {
      setPoolNotice({
        severity: 'error',
        title: extractPoolsError(err, 'Failed to add worker to pool'),
      });
    }
  };

  const addToPoolItem =
    skillPool && !worker.dismissType
      ? MenuItemsObject.addToPool(handleAddToPool)
      : undefined;

  const getMenuItems = () => {
    if (isNoShow) {
      return tsStatus
        ? [
            MenuItemsObject.showMap(handleMapViewModal),
            MenuItemsObject.cancelNoshow(handleToggleShowed),
            MenuItemsObject.viewTimesheetLogs(handleViewTimesheetLogs),
          ]
        : [MenuItemsObject.cancelNoshow(handleToggleShowed)];
    }

    if (isRemovedWorker && tsStatus && isClientAdmin) {
      const removedItems: React.ReactNode[] = [];
      // Add undismiss worker if worker is already dismissed
      if (worker.dismissType) {
        removedItems.push(
          MenuItemsObject.undismissWorker(handleUndismissWorker),
        );
      }
      removedItems.push(
        MenuItemsObject.addComment(handleAddComment, !showRating),
        MenuItemsObject.viewTimesheetLogs(handleViewTimesheetLogs),
      );
      return removedItems.filter((i) => i);
    }

    if (!tsStatus) {
      const baseItems: React.ReactNode[] = [];
      // Add undismiss worker if worker is already dismissed
      if (worker.dismissType) {
        baseItems.push(MenuItemsObject.undismissWorker(handleUndismissWorker));
      }
      // Add dismiss worker with special rules for LTA jobs
      if (canShowDismissWorkerOption) {
        baseItems.push(MenuItemsObject.dismissWorker(handleDismissWorker));
      }
      // Add removed from assignment if job is LTA and worker is not dismissed
      if (canShowRemoveFromAssignment) {
        baseItems.push(
          MenuItemsObject.removedFromAssignment(handleRemovedFromAssignment),
        );
      }

      return jobStatus === JobStatus.UPCOMING
        ? [...baseItems, MenuItemsObject.removeWorker(handleRemoveTimesheet)]
        : [
            ...baseItems,
            MenuItemsObject.noShow(handleToggleShowed),
            MenuItemsObject.viewTimesheetLogs(handleViewTimesheetLogs),
          ];
    }

    if (isClientAdmin) {
      return GetMenuItemsForClientAdmin(tsStatus)?.filter((i) => i);
    } else {
      return GetMenuItemsForTenantAdmin(tsStatus)?.filter((i) => i);
    }
  };

  const GetMenuItemsForTenantAdmin = (timesheetStatus) => {
    const baseItems: React.ReactNode[] = [];
    // Add undismiss worker if worker is already dismissed
    if (worker.dismissType) {
      baseItems.push(MenuItemsObject.undismissWorker(handleUndismissWorker));
    }
    // Add dismiss worker with special rules for LTA jobs
    if (canShowDismissWorkerOption) {
      baseItems.push(MenuItemsObject.dismissWorker(handleDismissWorker));
    }
    // Add removed from assignment if job is LTA and worker is not dismissed
    if (canShowRemoveFromAssignment) {
      baseItems.push(
        MenuItemsObject.removedFromAssignment(handleRemovedFromAssignment),
      );
    }

    switch (timesheetStatus) {
      case TimeSheetStatus.INCOMPLETE:
        return [
          ...baseItems,
          addToPoolItem,
          MenuItemsObject.showMap(handleMapViewModal),
          MenuItemsObject.addComment(handleAddComment, !showRating),
          MenuItemsObject.viewTimesheetLogs(handleViewTimesheetLogs),
        ];
      case TimeSheetStatus.PENDING:
        return [
          ...baseItems,
          addToPoolItem,
          MenuItemsObject.showMap(handleMapViewModal),
          MenuItemsObject.addComment(handleAddComment, !showRating),
          MenuItemsObject.viewTimesheetLogs(handleViewTimesheetLogs),
        ];
      case TimeSheetStatus.APPROVED:
        return [
          ...baseItems,
          addToPoolItem,
          MenuItemsObject.showMap(handleMapViewModal),
          MenuItemsObject.addComment(handleAddComment, !showRating),
          MenuItemsObject.viewTimesheetLogs(handleViewTimesheetLogs),
        ];
      case TimeSheetStatus.REJECTED:
        return [
          ...baseItems,
          addToPoolItem,
          MenuItemsObject.noShow(handleToggleShowed),
          MenuItemsObject.showMap(handleMapViewModal),
          MenuItemsObject.addComment(handleAddComment, !showRating),
          MenuItemsObject.viewTimesheetLogs(handleViewTimesheetLogs),
        ];
      default:
        break;
    }
  };

  const GetMenuItemsForClientAdmin = (timesheetStatus) => {
    const baseItems: React.ReactNode[] = [];
    // Add undismiss worker if worker is already dismissed
    if (worker.dismissType) {
      baseItems.push(MenuItemsObject.undismissWorker(handleUndismissWorker));
    }
    // Add dismiss worker with special rules for LTA jobs
    if (canShowDismissWorkerOption) {
      baseItems.push(MenuItemsObject.dismissWorker(handleDismissWorker));
    }
    // Add removed from assignment if job is LTA and worker is not dismissed
    if (canShowRemoveFromAssignment) {
      baseItems.push(
        MenuItemsObject.removedFromAssignment(handleRemovedFromAssignment),
      );
    }

    switch (timesheetStatus) {
      case TimeSheetStatus.INCOMPLETE:
        return [
          ...baseItems,
          MenuItemsObject.showMap(handleMapViewModal),
          MenuItemsObject.addComment(handleAddComment, !showRating),
          MenuItemsObject.viewTimesheetLogs(handleViewTimesheetLogs),
        ];
      case TimeSheetStatus.PENDING:
        return [
          ...baseItems,
          MenuItemsObject.addTip(showAddTipModal),
          MenuItemsObject.showMap(handleMapViewModal),
          MenuItemsObject.addComment(handleAddComment, !showRating),
          MenuItemsObject.viewTimesheetLogs(handleViewTimesheetLogs),
        ];
      case TimeSheetStatus.APPROVED:
        return [
          ...baseItems,
          MenuItemsObject.showMap(handleMapViewModal),
          MenuItemsObject.addComment(handleAddComment, !showRating),
          MenuItemsObject.viewTimesheetLogs(handleViewTimesheetLogs),
        ];
      case TimeSheetStatus.REJECTED:
        return [
          ...baseItems,
          MenuItemsObject.noShow(handleToggleShowed),
          MenuItemsObject.showMap(handleMapViewModal),
          MenuItemsObject.addComment(handleAddComment, !showRating),
          MenuItemsObject.viewTimesheetLogs(handleViewTimesheetLogs),
        ];
      default:
        break;
    }
  };

  const getChip = () => {
    if (isRemovedWorker) {
      return RemovedWorkerChip;
    } else if (isNoShow) {
      return NoShowChip;
    } else if (isConfirmed && !isClientAdmin) {
      return ConfirmedChip;
    } else {
      return HiredChip;
    }
  };

  const MENU_ITEMS = getMenuItems();
  const disabledCheckbox =
    isNoShow ||
    (!timesheet && jobStatus !== JobStatus.COMPLETED) ||
    isRemovedWorker ||
    (isClientAdmin && !enableCheckboxForClient(timesheet, jobStatus));

  const disableEdit =
    isNoShow || (!timesheet && jobStatus !== JobStatus.COMPLETED);

  const totalTime =
    timesheet && checkOut
      ? approvedTime > 0
        ? getRoundOff(approvedTime)
        : approvedTime
      : '-';

  const getName = (worker) => {
    let fullName = '';
    const user = worker.noShowMarkedBy;
    if (user) {
      fullName += user.firstName;
      if (user.middleName !== null && user.middleName !== undefined) {
        fullName += ` ${user.middleName}`;
      }
      fullName += ` ${user.lastName}`;
    }
    return fullName;
  };

  return (
    <React.Fragment key={worker?.id}>
      {phoneOnly ? (
        <Stack>
          {worker.showed ? (
            <WorkerMobileView
              checkboxIds={checkboxIds}
              disableCheckbox={disabledCheckbox}
              disableEdit={disableEdit}
              handleAddComment={showCommentsModal}
              handleApproveTimesheet={handleApproveTimesheet}
              handleEditTimesheet={
                disableEdit ? undefined : handleEditTimesheet
              }
              isClientAdmin={isClientAdmin}
              isSelecting={isSelecting}
              job={job}
              jobStatus={jobStatus}
              menuItems={MENU_ITEMS}
              setCheckboxIds={setCheckboxIds}
              setIsSelecting={setIsSelecting}
              timesheet={timesheet}
              totalTime={totalTime}
              workerInfo={{ ...workerInfo, workerId: worker.id }}
              workerStatusChip={getChip()}
              relationshipStatus={relationshipStatus}
              createWorkerRelationship={createWorkerRelationship}
              deleteWorkerRelationship={deleteWorkerRelationship}
              isMissingTimesheet={!!worker.isMissingTimesheet}
              isMissingCheckout={!!worker.isMissingCheckout}
            />
          ) : (
            <WorkerMobileDisableView
              isClientAdmin={isClientAdmin}
              job={job}
              menuItems={MENU_ITEMS}
              workerInfo={worker.worker}
              workerStatus={'NoShow'}
              workerStatusChip={getChip()}
              relationshipStatus={relationshipStatus}
              createWorkerRelationship={createWorkerRelationship}
              deleteWorkerRelationship={deleteWorkerRelationship}
            />
          )}
        </Stack>
      ) : (
        <>
          <TableRow
            key={worker.id}
            aria-haspopup="true"
            aria-owns={open ? 'mouse-over-popover' : undefined}
            // A No Show worker is highlighted so the person the admin came here
            // to find is identifiable without hunting the roster — the last step
            // of the Dashboard V2 No Show investigation path (GRAVY-10317,
            // Figma 37883:60811).
            //
            // The background was a hardcoded inline `white`, which no status
            // could override — an inline style beats any stylesheet — so this is
            // an emotion rule keyed off the authoritative status instead. The
            // red NoShowChip already renders via getChip(); this adds the light
            // red fill and the border around the whole row.
            //
            // GRAVY-10453 adds the same shape for the deep-linked worker, and
            // No Show WINS when a row is both: they are two different claims
            // in two near-identical reds, and showing one is far better than
            // blending them into a colour that means neither. The
            // `data-location-flagged` attribute is still set either way, so the
            // row stays findable by a test and by ops' own eye.
            css={
              isNoShow
                ? rowOutline(NO_SHOW_BG, NO_SHOW_BORDER)
                : isFlagFocused
                  ? rowOutline(FLAG_FOCUS_BG, FLAG_FOCUS_BORDER)
                  : { backgroundColor: 'white' }
            }
            data-no-show={isNoShow ? 'true' : undefined}
            data-location-flagged={isFlagFocused ? 'true' : undefined}
            onMouseEnter={handlePopoverOpen}
            onMouseLeave={handlePopoverClose}
            ref={rowRef}
          >
            <TableCell>
              <Checkbox
                checked={checkboxIds.includes(worker.id)}
                checkedIcon={<CheckBoxOutlined htmlColor="#332F2D" />}
                disabled={disabledCheckbox}
                size="small"
                style={{ padding: 0 }}
                onChange={handleCheckClick}
              />
            </TableCell>
            <TableCell css={{ paddingLeft: '12px', maxWidth: '320px' }}>
              <RenderWorkerInfo
                StatusChip={getChip()}
                handleAddComment={timesheet && showCommentsModal}
                isClientAdmin={isClientAdmin}
                relationshipStatus={relationshipStatus}
                job={job}
                createWorkerRelationship={createWorkerRelationship}
                deleteWorkerRelationship={deleteWorkerRelationship}
                timeSheet={timesheet}
                workerInfo={workerInfo}
                isMissingTimesheet={!!worker.isMissingTimesheet}
                isMissingCheckout={!!worker.isMissingCheckout}
              />
            </TableCell>

            <TableCell>
              <WorkerTimesheet
                disableEdit={disableEdit}
                handleAddComment={showCommentsModal}
                handleApproveTimesheet={handleApproveTimesheet}
                handleEditTimesheet={
                  disableEdit ? undefined : handleEditTimesheet
                }
                isClientAdmin={isClientAdmin}
                isSelecting={isSelecting}
                job={job}
                jobStatus={jobStatus}
                phoneOnly={false}
                setIsSelecting={setIsSelecting}
                timesheet={timesheet}
                totalTime={totalTime}
                workerInfo={{ ...workerInfo, workerId: worker.id }}
                workerStatusChip={getChip()}
                isMissingTimesheet={!!worker.isMissingTimesheet}
                isMissingCheckout={!!worker.isMissingCheckout}
              />
            </TableCell>

            <TableCell>
              <PermissionComponent id="timesheet-options-menu">
                <IconButton
                  aria-label="menu-icon"
                  disabled={!(MENU_ITEMS && MENU_ITEMS.length)}
                  id="menu-icon"
                  onClick={(e) => setAnchorEl(e.currentTarget)}
                >
                  <MoreVert />
                </IconButton>
              </PermissionComponent>
              <Menu
                MenuListProps={{
                  'aria-labelledby': 'basic-button',
                }}
                PaperProps={{
                  sx: {
                    border: '1px solid #D3D3D3',
                    filter:
                      'drop-shadow(0px 4.050473213195801px 4.050473213195801px rgba(0, 0, 0, 0.05))',
                    boxShadow: 'none',
                  },
                }}
                anchorEl={anchorEl}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                id="basic-menu"
                open={open}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                onClose={handleCloseMenu}
              >
                <MenuList
                  dense={true}
                  sx={{ paddingTop: '0px', paddingBottom: '0px' }}
                >
                  {MENU_ITEMS}
                </MenuList>
              </Menu>
            </TableCell>
          </TableRow>
          {!isClientAdmin && isNoShow && workerInfo && reason && (
            <CustomTooltipJob
              anchorEl={anchorTooltip}
              eventDate={worker.noShowMarkedAt}
              eventDoneBy={getName(worker)}
              handlePopoverClose={handlePopoverClose}
              isDropped={false}
              isNoShow={isNoShow}
              open={openTooltip}
              reason={reason ?? ''}
            />
          )}
        </>
      )}

      {error && (
        <Snackbar
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          autoHideDuration={4000}
          open={true}
          onClose={handleClose}
          sx={{ mt: 8 }}
        >
          <Alert severity="error" sx={{ width: '100%' }} onClose={handleClose}>
            <AlertTitle>Error: {error}</AlertTitle>
          </Alert>
        </Snackbar>
      )}

      {poolNotice && (
        <Snackbar
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          autoHideDuration={4000}
          open={true}
          onClose={() => setPoolNotice(null)}
          sx={{ mt: 8 }}
        >
          <Alert
            severity={poolNotice.severity}
            sx={{ width: '100%' }}
            onClose={() => setPoolNotice(null)}
          >
            <AlertTitle>{poolNotice.title}</AlertTitle>
            {poolNotice.body}
          </Alert>
        </Snackbar>
      )}
    </React.Fragment>
  );
};

export default HiredWorkerRow;
